"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { X, Check, Sparkles, Loader2, RefreshCw, Apple } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { plans } from "@/lib/data/subscriptions";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { shouldUseAppleIAP } from "@/lib/payments/platform-payment";
import { useIOSIAP } from "@/hooks/use-ios-iap";
import { IosSubscriptionLegalLinks } from "@/components/subscription/ios-subscription-legal";
import { SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE } from "@/lib/payments/subscription-user-messages";
import { IAP_PAYWALL_DEBUG_PANEL } from "@/lib/payments/iap-debug";
import { useTranslations } from "@/hooks/use-translations";
import { useFriendlyError } from "@/hooks/useFriendlyError";

interface PaywallProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradePro: () => void;
  onUpgradeElite: () => void;
}

export function Paywall({ isOpen, onClose, onUpgradePro, onUpgradeElite }: PaywallProps) {
  const { refreshMembership } = useAuthStore();
  const router = useRouter();
  const { t } = useTranslations("paywall");
  const friendlyError = useFriendlyError();
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productsUnavailable, setProductsUnavailable] = useState(false);
  const [purchasingPlanId, setPurchasingPlanId] = useState<string | null>(null);
  const isIOS = shouldUseAppleIAP();
  const {
    loading: iapLoading,
    purchaseSubscription,
    restorePurchases,
    iapCatalog,
    iapCatalogLoading,
    refreshIapCatalog,
    iapDiagnostics,
  } = useIOSIAP();

  const subscriptionPeriodLabel = (frequency: string | undefined) => {
    if (frequency === "monthly") return t("monthAutoRenewing");
    if (frequency === "yearly") return t("yearAutoRenewing");
    return "";
  };

  const iapPriceLabel = (planId: string, fallback: string) =>
    iapCatalog?.localizedPriceByPlanId[planId] ?? fallback;

  /** Only block while a purchase is in flight or initial native bridge/catalog has not returned once. */
  const isPlanIapBlocked = (_planId: string) =>
    Boolean(isIOS && iapCatalogLoading && !iapCatalog);
  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Get plans based on billing cycle
  const premiumPlan = plans.find(
    (p) => p.name === "Premium" && p.frequency === billing
  );
  const elitePlan = plans.find(
    (p) => p.name === "Elite" && p.frequency === billing
  );

  // Get feature lists - for yearly plans, combine monthly features with yearly-specific benefits
  const getFeaturesForPlan = (plan: typeof premiumPlan | typeof elitePlan) => {
    if (!plan) return [];
    
    if (plan.frequency === "yearly") {
      // For yearly plans, get monthly features + yearly-specific benefits
      const monthlyPlan = plans.find(
        (p) => p.name === plan.name && p.frequency === "monthly"
      );
      const monthlyFeatures = monthlyPlan?.benefits || [];
      
      // Filter out "Everything in X Monthly" from yearly benefits and combine
      const yearlySpecificBenefits = plan.benefits.filter(
        (benefit) => !benefit.toLowerCase().includes("everything in")
      );
      
      return [...monthlyFeatures, ...yearlySpecificBenefits];
    }
    
    // For monthly plans, just return their benefits
    return plan.benefits || [];
  };

  const premiumFeatures = getFeaturesForPlan(premiumPlan);
  const eliteFeatures = getFeaturesForPlan(elitePlan);

  const handleUpgrade = async (plan: typeof premiumPlan | typeof elitePlan) => {
    if (!plan) return;

    if (isIOS) {
      // Apple App Store Guideline 3.1.1: use IAP for digital subscriptions on iOS
      setError(null);
      setProductsUnavailable(false);
      setPurchasingPlanId(plan.id);
      try {
        const result = await purchaseSubscription(plan.id);
        if (result.success) {
          onClose();
          if (plan.name === "Premium") onUpgradePro();
          else if (plan.name === "Elite") onUpgradeElite();
        } else if (!result.error?.toLowerCase().includes("cancel")) {
          if (result.productsUnavailable) {
            setProductsUnavailable(true);
          } else {
            setError(result.error ?? SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE);
          }
        }
      } finally {
        setPurchasingPlanId(null);
      }
      return;
    }

    // Web / Android — use Stripe checkout
    onClose();
    router.push(`/checkout?plan=${plan.id}`);
    if (plan.name === "Premium") onUpgradePro();
    else if (plan.name === "Elite") onUpgradeElite();
  };

  const handleRefreshMembership = async () => {
    setRefreshing(true);
    setError(null);

    try {
      await refreshMembership();
      // Close the paywall on success
      onClose();
    } catch (err: any) {
      console.error("Error refreshing membership:", err);
      setError(friendlyError(err, t("failedToRefreshMembership")));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 md:p-6"
      onClick={onClose}
      style={{ 
        paddingTop: 'max(4rem, calc(4rem + env(safe-area-inset-top, 0px)))',
        paddingBottom: 'max(1rem, calc(1rem + env(safe-area-inset-bottom, 0px)))'
      }}
    >
      <Card
        className="w-full max-w-5xl max-h-[calc(100vh-6rem)] overflow-y-auto bg-white relative"
        onClick={(e) => e.stopPropagation()}
        padding="lg"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100 transition-colors z-10"
          aria-label={t("close")}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <Logo variant="compact" className="h-16 w-16" href={undefined} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-3">
            {t("unlockTitle")}
          </h1>
          <p className="text-base md:text-lg text-neutral-600 max-w-2xl mx-auto">
            {t("unlockSubtitle")}
          </p>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setBilling("monthly")}
              className={buttonClasses({
                variant: billing === "monthly" ? "primary" : "ghost",
                size: "sm",
              })}
            >
              {t("monthly")}
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={buttonClasses({
                variant: billing === "yearly" ? "primary" : "ghost",
                size: "sm",
              })}
            >
              {t("yearly")}
            </button>
          </div>
        </div>

        {isIOS && IAP_PAYWALL_DEBUG_PANEL && (
          <div className="mb-4 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 font-mono text-[11px] leading-snug text-neutral-800">
            <p className="mb-1 font-semibold text-neutral-600">IAP debug (iOS, temporary)</p>
            <p>requested IDs: {iapDiagnostics.requestedAppleProductIds.join(", ")}</p>
            <p>products returned: {iapDiagnostics.returnedProductCount}</p>
            <p>
              PM / PY / EM / EY resolved:{" "}
              {[
                iapDiagnostics.resolvedPremiumMonthly,
                iapDiagnostics.resolvedPremiumYearly,
                iapDiagnostics.resolvedEliteMonthly,
                iapDiagnostics.resolvedEliteYearly,
              ]
                .map((x) => (x ? "Y" : "n"))
                .join(" / ")}
            </p>
            <p>store init: {iapDiagnostics.storeInitCompleted ? "ok" : "no"}</p>
            {iapDiagnostics.storeInitError ? (
              <p className="text-amber-900">store init err: {iapDiagnostics.storeInitError}</p>
            ) : null}
            {iapDiagnostics.catalogFetchError ? (
              <p className="text-amber-900">catalog err: {iapDiagnostics.catalogFetchError}</p>
            ) : null}
            <p>catalog loading: {iapDiagnostics.iapCatalogLoading ? "yes" : "no"}</p>
            <p>purchases blocked (no offers): {iapDiagnostics.purchasesBlockedNoOffers ? "yes" : "no"}</p>
          </div>
        )}

        {productsUnavailable && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm text-blue-800 mb-2">
              {SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE}
            </p>
            <button
              type="button"
              onClick={() => void refreshIapCatalog()}
              disabled={iapCatalogLoading}
              className="text-sm font-medium text-primary underline underline-offset-2 disabled:opacity-50"
            >
              {iapCatalogLoading ? t("loading") : t("retry")}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && !productsUnavailable && (
          <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <p className="text-sm text-neutral-800">{error}</p>
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Premium Plan */}
          {premiumPlan && (
            <Card
              className="relative border-2 border-neutral-200 hover:border-primary/30 transition-all duration-300"
              padding="lg"
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">Premium</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-neutral-900">
                      {iapPriceLabel(premiumPlan.id, premiumPlan.priceLabel)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-500">
                    {billing === "monthly" ? t("monthly") : t("yearly")} {t("subscription")} ·{" "}
                    {subscriptionPeriodLabel(premiumPlan.frequency)}
                    {isIOS && iapCatalogLoading ? ` · ${t("loadingPrice")}` : ""}
                  </p>
                </div>

                <ul className="space-y-3">
                  {premiumFeatures.slice(0, 8).map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm text-neutral-700 leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleUpgrade(premiumPlan)}
                  variant="primary"
                  size="md"
                  className="w-full"
                  disabled={
                    iapLoading || !!purchasingPlanId || isPlanIapBlocked(premiumPlan.id)
                  }
                >
                  {purchasingPlanId === premiumPlan.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("processing")}</>
                  ) : isIOS ? (
                    <><Apple className="w-4 h-4 mr-2" />{t("subscribeWithApple")}</>
                  ) : t("upgradeNow")}
                </Button>
              </div>
            </Card>
          )}

          {/* Elite Plan */}
          {elitePlan && (
            <Card
              className="relative border-2 border-primary bg-gradient-to-br from-primary/5 to-primary/10 hover:border-primary transition-all duration-300"
              padding="lg"
            >
              {/* Recommended Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t("recommended")}
                </span>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-2xl font-bold text-neutral-900">Elite</h3>
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-neutral-900">
                      {iapPriceLabel(elitePlan.id, elitePlan.priceLabel)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-500">
                    {billing === "monthly" ? t("monthly") : t("yearly")} {t("subscription")} ·{" "}
                    {subscriptionPeriodLabel(elitePlan.frequency)}
                    {isIOS && iapCatalogLoading ? ` · ${t("loadingPrice")}` : ""}
                  </p>
                </div>

                <ul className="space-y-3">
                  {eliteFeatures.slice(0, 8).map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm text-neutral-700 leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleUpgrade(elitePlan)}
                  variant="primary"
                  size="md"
                  className="w-full"
                  disabled={iapLoading || !!purchasingPlanId || isPlanIapBlocked(elitePlan.id)}
                >
                  {purchasingPlanId === elitePlan.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("processing")}</>
                  ) : isIOS ? (
                    <><Apple className="w-4 h-4 mr-2" />{t("subscribeWithApple")}</>
                  ) : t("upgradeNow")}
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Footer: subscription disclosure + legal links + Restore (Apple 3.1.2(c) / 3.1.1) */}
        <div className="mt-6 w-full min-w-0 px-4 sm:px-6">
          {isIOS ? (
            <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
              <div className="min-w-0 flex-1">
                <IosSubscriptionLegalLinks />
              </div>
              <button
                type="button"
                onClick={restorePurchases}
                disabled={iapLoading}
                className="text-sm text-primary hover:text-primary-dark font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex shrink-0 items-center gap-2 self-start min-h-[44px] px-1 sm:px-2 sm:pt-0.5"
              >
                {iapLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                    <span>{t("restoring")}</span>
                  </>
                ) : (
                  <span>{t("restorePurchases")}</span>
                )}
              </button>
            </div>
          ) : (
            <p className="text-center text-xs text-neutral-500">
              {t("upgradesProcessedSecurely")}
            </p>
          )}
        </div>

        {/* Refresh Membership Link */}
        <div className="text-center mt-4">
          <button
            onClick={handleRefreshMembership}
            disabled={refreshing}
            className="text-sm text-primary hover:text-primary-dark font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
          >
            {refreshing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t("refreshing")}</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>{t("alreadyUpgradedTapToRefresh")}</span>
              </>
            )}
          </button>
        </div>
      </Card>
    </div>
  );
}

